import { createContext, useContext } from 'react'
import type {
  CrmChatProviderKey,
  CrmLeadIntegrationChatTab,
} from '@/api/types/crm/crmLeadFunnels.types'
import type { IntegrationChatDockScreen } from './integrationChatDockPersistence'
import type { IntegrationChatGlobalUnreadSnapshot, IntegrationChatUnreadByIntegrationId } from './integrationChatGlobalUnread'

export type IntegrationChatDockOpenOptions = {
  initialUnreadCount?: number
  leadVkUpdatedAt?: string | null
  conversationId?: number | null
  communityTabs?: CrmLeadIntegrationChatTab[]
  provider?: CrmChatProviderKey
  onClose?: () => void
  onMarkReadSuccess?: (leadId: number, conversationId?: number) => void
}

export type IntegrationChatDockContextValue = {
  leadId: number | null
  conversationId: number | null
  communityTabs: CrmLeadIntegrationChatTab[]
  activeProvider: CrmChatProviderKey | null
  chatsListIntegrationId: number | null
  chatsListExternalChannelId: string | null
  expanded: boolean
  chatScreen: IntegrationChatDockScreen
  launcherUnreadCount: number
  /** Непрочитанные по лидам текущего менеджера (my_total). */
  launcherMyUnreadCount: number
  providerUnreadCounts: Record<CrmChatProviderKey, number>
  providerMyUnreadCounts: Record<CrmChatProviderKey, number>
  /** Unread по integration_id (аккаунты Wazzup / табы ботов). */
  unreadByIntegrationId: IntegrationChatUnreadByIntegrationId
  myUnreadByIntegrationId: IntegrationChatUnreadByIntegrationId
  openDock: (leadId: number, options?: IntegrationChatDockOpenOptions) => void
  switchDockLead: (
    leadId: number,
    options?: {
      conversationId?: number | null
      provider?: CrmChatProviderKey
    },
  ) => void
  closeDock: () => void
  toggleExpanded: () => void
  toggleLauncher: () => void
  setChatScreen: (screen: IntegrationChatDockScreen) => void
  openProviderChats: (provider: CrmChatProviderKey) => void
  openWazzupAccountChannels: (integrationId: number) => void
  openWazzupChannelChats: (
    integrationId: number,
    externalChannelId: string,
  ) => void
  backToIntegrationsPicker: () => void
  backToWazzupAccounts: () => void
  backToWazzupChannels: () => void
  /** Из диалога в доке — к списку чатов канала (CrmIntegrationChatsList). */
  backToProviderChatsList: () => void
  setConversationId: (conversationId: number | null) => void
  setChatsListIntegrationId: (integrationId: number | null) => void
  applyGlobalUnread: (snapshot: IntegrationChatGlobalUnreadSnapshot) => void
  /** @deprecated use applyGlobalUnread */
  applyLauncherUnread: (count: number) => void
}

export type IntegrationChatDockNotifyContextValue = {
  notifyMarkReadSuccess: (leadId: number, conversationId?: number) => void
}

export const IntegrationChatDockContext =
  createContext<IntegrationChatDockContextValue | null>(null)

export const IntegrationChatDockNotifyContext =
  createContext<IntegrationChatDockNotifyContextValue | null>(null)

export const useIntegrationChatDockNotify = () => {
  const ctx = useContext(IntegrationChatDockNotifyContext)
  if (!ctx) {
    throw new Error(
      'useIntegrationChatDockNotify must be used within IntegrationChatDockProvider',
    )
  }
  return ctx
}

export const useIntegrationChatDock = (): IntegrationChatDockContextValue => {
  const ctx = useContext(IntegrationChatDockContext)
  if (!ctx) {
    throw new Error(
      'useIntegrationChatDock must be used within IntegrationChatDockProvider',
    )
  }
  return ctx
}
